import { useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import { useConversionTracking } from "@/hooks/useConversionTracking";
import { emitConsentSnapshot } from "@/lib/consentEmitter";

/**
 * Automatically tracks page views and time on page
 * Add this component inside BrowserRouter to track all navigation
 *
 * Also re-emits Consent Mode v2 snapshot (`default` + `update` + `cookie_consent_update`)
 * on every route change so GTM can use `cookie_consent_update` as a page-view trigger.
 * The very first run (hard reload) skips the extra `default` because index.html
 * already pushed it inline.
 */
export const PageViewTracker = () => {
  const location = useLocation();
  const { trackPageView, trackTimeOnPage } = useConversionTracking();
  const hasTrackedRef = useRef<string | null>(null);
  const pageLoadTimeRef = useRef<number>(Date.now());
  const isFirstRunRef = useRef<boolean>(true);

  useEffect(() => {
    // Track page view on route change
    const currentPath = location.pathname + location.search;
    
    // Avoid double-tracking on same page
    if (hasTrackedRef.current !== currentPath) {
      // Track time on previous page before switching
      if (hasTrackedRef.current !== null) {
        trackTimeOnPage();
      }

      // Track new page view
      trackPageView();
      hasTrackedRef.current = currentPath;
      pageLoadTimeRef.current = Date.now();

      // Re-emit consent snapshot for GTM page-view trigger.
      // First run = hard reload: index.html already pushed `default`, so skip it.
      // Subsequent runs = SPA navigation: include `default` to re-state baseline.
      emitConsentSnapshot({
        includeDefault: !isFirstRunRef.current,
        pagePath: currentPath,
      });
      isFirstRunRef.current = false;
    }
  }, [location.pathname, location.search, trackPageView, trackTimeOnPage]);

  // Track time on page when leaving/closing
  useEffect(() => {
    const handleBeforeUnload = () => {
      trackTimeOnPage();
    };

    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, [trackTimeOnPage]);

  return null;
};

export default PageViewTracker;
